import { useClient } from '@cortex/react-query';
import type { IPaginated, Workflow, WorkflowListItem } from '@cortex/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, type CSSProperties } from 'react';

import { EmptyState } from '../../components/common/EmptyState';
import { LoadMoreButton } from '../../components/common/LoadMoreButton';
import { Modal } from '../../components/common/Modal';
import { PageHeader } from '../../components/common/PageHeader';
import { Sparkline } from '../../components/common/Sparkline';
import { Spinner } from '../../components/common/Spinner';
import { TagEditor } from '../../components/common/TagEditor';
import { ViewToggle } from '../../components/common/ViewToggle';
import { MissingAuthBadge } from '../../components/workflows/MissingAuthBadge';
import { WorkflowDisabledBadge } from '../../components/workflows/WorkflowDisabledBadge';
import { WorkflowManagedBadge } from '../../components/workflows/WorkflowManagedBadge';
import { WorkflowMiniGraph } from '../../components/workflows/WorkflowMiniGraph';
import { routes } from '../../lib/constants';
import { formatRelative } from '../../lib/format';
import { controlsRow, sortButtonStyle, tdStyle, thStyle } from '../../lib/table-styles';
import { usePaginatedList } from '../../lib/use-paginated-list';
import { useUpdateWorkflowTags } from '../../lib/use-workflow-tags';
import { useViewMode } from '../../lib/view-mode';

export const Route = createFileRoute('/workflows/')({ component: WorkflowsPage });

const grid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: 'var(--sp-3)',
};

const cardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
  padding: 'var(--sp-3)',
  background: 'var(--surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius)',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.18), 0 1px 2px rgba(0, 0, 0, 0.24)',
  cursor: 'pointer',
  textDecoration: 'none',
  color: 'inherit',
  transition: 'box-shadow 120ms ease, transform 120ms ease, border-color 120ms ease',
};

const cardHeaderRow: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 'var(--sp-2)',
};

const titleStyle: CSSProperties = {
  fontSize: 'var(--fs-sm)',
  fontWeight: 'var(--fw-med)',
  color: 'var(--text-primary)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
};

const descStyle: CSSProperties = {
  fontSize: 'var(--fs-xs)',
  color: 'var(--text-secondary)',
  margin: 0,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  lineHeight: 1.4,
  minHeight: '2.6em',
};

const graphBoxStyle: CSSProperties = {
  background: 'var(--surface-2)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
  padding: 'var(--sp-1)',
};

const footerRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontSize: 'var(--fs-xs)',
  color: 'var(--text-secondary)',
  gap: 'var(--sp-2)',
};

const deleteBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '20px',
  height: '20px',
  padding: 0,
  background: 'transparent',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  fontSize: 'var(--fs-xs)',
  lineHeight: 1,
};

function WorkflowsPage() {
  const client = useClient();
  const qc = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState<WorkflowListItem | null>(null);
  const [typed, setTyped] = useState('');
  const [copied, setCopied] = useState(false);
  // List is the default layout — cohesive with the /evals entity + runs
  // tables — with the card grid available behind the toggle. The choice
  // persists to localStorage so it survives navigation and reload.
  const [viewMode, setViewMode] = useViewMode('workflows.viewMode', 'list');

  const list = usePaginatedList<WorkflowListItem>({
    queryKey: ['workflows'],
    fetchPage: ({ limit, offset }) =>
      client.http
        .get(`v1/workflows?limit=${limit}&offset=${offset}`)
        .json<IPaginated<WorkflowListItem>>(),
  });

  const remove = useMutation({
    mutationFn: (id: string) => client.http.delete(`v1/workflows/${id}`).text(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['workflows'] });
      setConfirmDelete(null);
      setTyped('');
    },
  });

  const closeModal = () => {
    if (remove.isPending) return;
    setConfirmDelete(null);
    setTyped('');
    setCopied(false);
    remove.reset();
  };

  const target = confirmDelete;
  const canDelete = target !== null && typed === target.name && !remove.isPending;

  return (
    <>
      <PageHeader title="Workflows" actions={<NewWorkflowButton />} />
      {list.isLoading ? <Spinner /> : null}
      {list.error ? (
        <div className="alert alert--danger">Failed to load: {list.error.message}</div>
      ) : null}
      {!list.isLoading && list.items.length === 0 ? (
        <EmptyState
          title="No workflows yet"
          description="Create your first workflow to start orchestrating agents and tools."
          cta={<NewWorkflowButton />}
        />
      ) : null}
      {list.items.length > 0 ? (
        <>
          <div style={controlsRow}>
            <ViewToggle value={viewMode} onChange={setViewMode} ariaLabel="Workflow layout" />
          </div>
          {viewMode === 'grid' ? (
            <div style={grid}>
              {list.items.map((w) => (
                <WorkflowCard
                  key={w.id}
                  workflow={w}
                  onDelete={() => {
                    setConfirmDelete(w);
                    setTyped('');
                    remove.reset();
                  }}
                />
              ))}
            </div>
          ) : (
            <WorkflowTable
              workflows={list.items}
              onDelete={(w) => {
                setConfirmDelete(w);
                setTyped('');
                remove.reset();
              }}
            />
          )}
          <LoadMoreButton
            loaded={list.items.length}
            total={list.total}
            hasMore={list.hasMore}
            isFetchingNextPage={list.isFetchingNextPage}
            onLoadMore={list.loadMore}
          />
        </>
      ) : null}

      <Modal
        open={target !== null}
        onClose={closeModal}
        eyebrow="DELETE WORKFLOW"
        title={target ? `Delete "${target.name}"?` : ''}
        width="min(480px, 92vw)"
        footer={
          target ? (
            <>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={closeModal}
                disabled={remove.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--destructive btn--sm"
                onClick={() => remove.mutate(target.id)}
                disabled={!canDelete}
              >
                {remove.isPending ? 'Deleting…' : 'Delete workflow'}
              </button>
            </>
          ) : null
        }
      >
        {target ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
              This will delete the workflow and its run history will no longer be accessible from
              the list. This action cannot be undone from the UI.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
              <label
                htmlFor="confirm-workflow-name"
                className="t-mono-label"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--sp-1)',
                  flexWrap: 'wrap',
                }}
              >
                <span>Type</span>
                <span
                  style={{
                    color: 'var(--text-primary)',
                    fontFamily: 'var(--font-mono, monospace)',
                    textTransform: 'none',
                  }}
                >
                  {target.name}
                </span>
                <button
                  type="button"
                  aria-label="Copy workflow name"
                  title={copied ? 'Copied' : 'Copy name'}
                  onClick={() => {
                    navigator.clipboard
                      .writeText(target.name)
                      .then(() => {
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1200);
                      })
                      .catch((err) => console.error(err));
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '18px',
                    height: '18px',
                    padding: 0,
                    background: 'transparent',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 'var(--radius-sm)',
                    color: copied ? 'var(--success, #4ade80)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                  }}
                >
                  {copied ? (
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="3 8 7 12 13 4" />
                    </svg>
                  ) : (
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <rect x="5" y="5" width="9" height="9" rx="1.5" />
                      <path d="M3 11V3a1 1 0 0 1 1-1h7" />
                    </svg>
                  )}
                </button>
                <span>to confirm</span>
              </label>
              <input
                id="confirm-workflow-name"
                className="input"
                autoFocus={true}
                autoComplete="off"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={target.name}
                disabled={remove.isPending}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canDelete) remove.mutate(target.id);
                }}
              />
            </div>
            {remove.error ? (
              <div className="field-error">{(remove.error as Error).message}</div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </>
  );
}

/**
 * Single button that creates a blank workflow and jumps the user straight
 * into the builder — replacing the old intermediate /workflows/new form
 * page (DEV-16159). Name + type default sensibly; both are editable in
 * the builder's top bar via `InlineEditableText` + the rename mutation,
 * so the form's only real job was a name prompt that we can defer.
 *
 * The builder is opened with the palette panel pre-selected so the user
 * can drop the first node immediately.
 */
function NewWorkflowButton() {
  const client = useClient();
  const qc = useQueryClient();
  const nav = useNavigate();

  const create = useMutation({
    mutationFn: () =>
      client.http
        .post('v1/workflows', {
          json: {
            name: `Untitled workflow · ${randomHex(4)}`,
            workflowType: 'GENERIC',
            graph: { nodes: [], edges: [] },
          },
        })
        .json<Workflow>(),
    onSuccess: async (w) => {
      try {
        await qc.invalidateQueries({ queryKey: ['workflows'] });
      } catch (err) {
        console.error(err);
      }
      try {
        await nav({ to: routes.workflowDetail(w.id), search: { panel: 'overview' } });
      } catch (err) {
        console.error(err);
      }
    },
  });

  return (
    <button
      type="button"
      className="btn btn--primary btn--sm"
      onClick={() => create.mutate()}
      disabled={create.isPending}
    >
      {create.isPending ? 'Creating…' : 'New workflow'}
    </button>
  );
}

// ── List view ────────────────────────────────────────────────────────────────

/**
 * Table view of the workflows list — the default layout. Column set,
 * `thStyle`/`tdStyle`, `.card` framing, row hover and click-to-navigate all
 * mirror the /evals runs table so the two pages read as siblings.
 */
// ── Sorting ──────────────────────────────────────────────────────────────────
// Interactive column sort, mirroring the Ground-truths table's caret + aria-sort
// pattern. Tags and the delete action aren't sortable.

type SortKey = 'name' | 'type' | 'nodes' | 'runs7d' | 'lastRun' | 'updated';
type SortDir = 'asc' | 'desc';

const nodeCountOf = (w: WorkflowListItem) => w.graphSummary?.nodes?.length ?? 0;
const runs7dOf = (w: WorkflowListItem) => (w.runTrend ?? []).reduce((s, d) => s + d.count, 0);
/** Epoch ms, or -Infinity for "never" so nulls sort to the bottom when desc. */
const timeOf = (v: string | Date | null | undefined) => (v ? new Date(v).getTime() : -Infinity);

/** Ascending comparator per key; direction is applied by the caller. */
function compareWorkflows(a: WorkflowListItem, b: WorkflowListItem, key: SortKey): number {
  switch (key) {
    case 'name':
      return a.name.localeCompare(b.name);
    case 'type':
      return a.workflowType.localeCompare(b.workflowType);
    case 'nodes':
      return nodeCountOf(a) - nodeCountOf(b);
    case 'runs7d':
      return runs7dOf(a) - runs7dOf(b);
    case 'lastRun':
      return timeOf(a.lastRunAt) - timeOf(b.lastRunAt);
    case 'updated':
      return timeOf(a.updatedAt) - timeOf(b.updatedAt);
  }
}

function SortableTh({
  label,
  sortKey,
  active,
  dir,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: SortKey;
  active: boolean;
  dir: SortDir;
  onSort: (k: SortKey) => void;
  align?: 'left' | 'right';
}) {
  return (
    <th
      style={{ ...thStyle, textAlign: align }}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        title={`Sort by ${label}`}
        style={{
          ...sortButtonStyle,
          flexDirection: align === 'right' ? 'row-reverse' : 'row',
          marginLeft: align === 'right' ? 'auto' : undefined,
        }}
      >
        {label}
        <span style={{ opacity: active ? 1 : 0.25, fontSize: 9 }}>
          {active ? (dir === 'asc' ? '▲' : '▼') : '▼'}
        </span>
      </button>
    </th>
  );
}

function WorkflowTable({
  workflows,
  onDelete,
}: {
  workflows: WorkflowListItem[];
  onDelete: (w: WorkflowListItem) => void;
}) {
  // Default: most-recently-updated first (matches the pre-sort from the API).
  const [sortKey, setSortKey] = useState<SortKey>('updated');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const onSort = (k: SortKey) => {
    if (k === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      // Text columns read best A→Z; numeric/date columns high→low first.
      setSortDir(k === 'name' || k === 'type' ? 'asc' : 'desc');
    }
  };

  const sorted = useMemo(() => {
    const arr = [...workflows];
    arr.sort((a, b) => {
      const cmp = compareWorkflows(a, b, sortKey);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [workflows, sortKey, sortDir]);

  const thProps = (key: SortKey) => ({
    sortKey: key,
    active: sortKey === key,
    dir: sortDir,
    onSort,
  });

  return (
    <div className="card">
      {/* Scroll horizontally rather than clip the trailing columns when the
          table is wider than the card (e.g. nav expanded on a narrow window). */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 880 }}>
          <thead>
            <tr>
              <SortableTh label="Workflow" {...thProps('name')} />
              <SortableTh label="Type" {...thProps('type')} />
              <th style={thStyle}>Tags</th>
              <SortableTh label="Nodes" align="right" {...thProps('nodes')} />
              <SortableTh label="Runs · 7d" {...thProps('runs7d')} />
              <SortableTh label="Last Run" {...thProps('lastRun')} />
              <SortableTh label="Updated" {...thProps('updated')} />
              <th style={{ ...thStyle, textAlign: 'right' }} aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((w) => (
              <WorkflowRow key={w.id} workflow={w} onDelete={() => onDelete(w)} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WorkflowRow({ workflow, onDelete }: { workflow: WorkflowListItem; onDelete: () => void }) {
  const nav = useNavigate();
  const updateTags = useUpdateWorkflowTags();
  const trend = workflow.runTrend ?? [];
  const counts = trend.map((d) => d.count);
  const totalRuns = counts.reduce((sum, c) => sum + c, 0);
  const nodeCount = workflow.graphSummary?.nodes?.length ?? 0;
  const edgeCount = workflow.graphSummary?.edges?.length ?? 0;
  return (
    <tr
      onClick={() => {
        nav({ to: routes.workflowDetail(workflow.id) }).catch((err) => console.error(err));
      }}
      style={{ cursor: 'pointer', borderTop: '1px solid var(--border-subtle)' }}
      onMouseEnter={(e) => {
        for (const td of e.currentTarget.children)
          (td as HTMLElement).style.background = 'rgba(44,65,66,0.08)';
      }}
      onMouseLeave={(e) => {
        for (const td of e.currentTarget.children) (td as HTMLElement).style.background = '';
      }}
    >
      <td style={tdStyle}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontWeight: 500, color: 'var(--text-primary)' }} title={workflow.name}>
            {workflow.name}
          </span>
          <WorkflowDisabledBadge
            disabledAt={workflow.disabledAt}
            managedRef={workflow.managedRef}
          />
          <WorkflowManagedBadge managedBy={workflow.managedBy} managedRef={workflow.managedRef} />
          <MissingAuthBadge hasMissingAuth={workflow.hasMissingAuth} />
        </div>
        {workflow.description ? (
          // Two lines, then an ellipsis: a one-line clamp at 420px cut a
          // managed workflow's description to "Managed b…".
          <div
            style={{
              fontSize: 11,
              color: 'var(--text-secondary)',
              marginTop: 2,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              maxWidth: 640,
              lineHeight: 1.35,
            }}
            title={workflow.description}
          >
            {workflow.description}
          </div>
        ) : null}
      </td>
      <td style={tdStyle}>
        <span className="badge badge--neutral">{workflow.workflowType}</span>
      </td>
      {/* Stop row-navigation while editing tags in the cell. */}
      <td style={{ ...tdStyle, maxWidth: 260 }} onClick={(e) => e.stopPropagation()}>
        <TagEditor
          tags={workflow.tags}
          ariaLabel={`Tags for ${workflow.name}`}
          onChange={(next) => updateTags.mutate({ workflowId: workflow.id, tags: next })}
        />
      </td>
      <td
        style={{
          ...tdStyle,
          textAlign: 'right',
          fontFamily: 'var(--font-mono)',
          fontSize: 13,
          color: 'var(--text-secondary)',
        }}
        title={`${nodeCount} nodes · ${edgeCount} edges`}
      >
        {nodeCount}
      </td>
      <td style={tdStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          {/* A trend needs more than one run to be one: a single run drew a
              rising slope that said nothing. */}
          {totalRuns >= 2 ? (
            <Sparkline
              values={counts}
              width={64}
              ariaLabel={`${totalRuns} runs in the last 7 days`}
            />
          ) : (
            <span style={{ width: 64, display: 'inline-block' }} aria-hidden="true" />
          )}
          <span
            style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-secondary)' }}
          >
            {totalRuns}
          </span>
        </div>
      </td>
      <td style={{ ...tdStyle, color: 'var(--text-secondary)', fontSize: 12 }}>
        {workflow.lastRunAt ? (
          formatRelative(workflow.lastRunAt)
        ) : (
          <span className="t-muted">Never</span>
        )}
      </td>
      <td style={{ ...tdStyle, color: 'var(--text-secondary)', fontSize: 12 }}>
        {formatRelative(workflow.updatedAt)}
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        {/* A managed workflow can't be deleted (the API answers 409) — it is
            disabled instead, from the editor. */}
        {workflow.managedBy ? null : (
          <button
            type="button"
            aria-label={`Delete ${workflow.name}`}
            title="Delete workflow"
            style={deleteBtnStyle}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete();
            }}
          >
            ✕
          </button>
        )}
      </td>
    </tr>
  );
}

function WorkflowCard({
  workflow,
  onDelete,
}: {
  workflow: WorkflowListItem;
  onDelete: () => void;
}) {
  return (
    <Link to={routes.workflowDetail(workflow.id)} style={cardStyle}>
      <div style={cardHeaderRow}>
        <span style={titleStyle} title={workflow.name}>
          {workflow.name}
        </span>
        <span
          style={{
            display: 'inline-flex',
            gap: 'var(--sp-1)',
            flexShrink: 0,
            alignItems: 'center',
          }}
        >
          <span className="badge badge--neutral">{workflow.workflowType}</span>
          <WorkflowManagedBadge managedBy={workflow.managedBy} managedRef={workflow.managedRef} />
          <MissingAuthBadge hasMissingAuth={workflow.hasMissingAuth} />
          {workflow.managedBy ? null : (
            <button
              type="button"
              aria-label={`Delete ${workflow.name}`}
              title="Delete workflow"
              style={deleteBtnStyle}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete();
              }}
            >
              ✕
            </button>
          )}
        </span>
      </div>
      <p style={descStyle}>
        {workflow.description ?? <span className="t-muted">No description</span>}
      </p>
      {(workflow.tags?.length ?? 0) > 0 ? (
        // Read-only in the card: the card is itself a navigation Link, so we
        // avoid nesting the editable input/remove buttons. Tags are edited
        // inline in the list view.
        <TagEditor tags={workflow.tags} readOnly={true} ariaLabel={`Tags for ${workflow.name}`} />
      ) : null}
      <div style={graphBoxStyle}>
        <WorkflowMiniGraph summary={workflow.graphSummary} />
      </div>
      <RunSparklineRow workflow={workflow} />
      <div style={footerRow}>
        <span>
          {workflow.lastRunAt ? (
            <>Last run {formatRelative(workflow.lastRunAt)}</>
          ) : (
            <span className="t-muted">Never run</span>
          )}
        </span>
        <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>
          {summarizeGraph(workflow)}
        </span>
      </div>
      <div style={footerRow}>
        <span>Updated {formatRelative(workflow.updatedAt)}</span>
      </div>
    </Link>
  );
}

const sparkRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--sp-2)',
};

const sparkLabel: CSSProperties = {
  fontSize: 'var(--fs-xs)',
  color: 'var(--text-secondary)',
  whiteSpace: 'nowrap',
};

/**
 * Trailing-7-day run-count sparkline for a card. `runTrend` is always a
 * length-7, oldest→newest, zero-filled series from the API; defensively
 * tolerate older cached rows that predate the field.
 */
function RunSparklineRow({ workflow }: { workflow: WorkflowListItem }) {
  const trend = workflow.runTrend ?? [];
  const counts = trend.map((d) => d.count);
  const total = counts.reduce((sum, c) => sum + c, 0);
  const label = `${total} run${total === 1 ? '' : 's'} · 7d`;
  return (
    <div style={sparkRow}>
      <span style={sparkLabel}>{label}</span>
      {/* Hidden under two runs: one run is a count, not a trend. */}
      {total >= 2 ? (
        <Sparkline values={counts} ariaLabel={`${total} runs in the last 7 days`} />
      ) : null}
    </div>
  );
}

function summarizeGraph(w: WorkflowListItem): string {
  const g = w.graphSummary;
  if (!g) return '—';
  // Defensive: stale cached rows from before DEV-16132 (or a partial API
  // response on a backend regression) can land here with `nodes` or
  // `edges` undefined. The Zod schema guarantees both at the type level,
  // but a runtime crash here breaks the whole `/workflows` page — and
  // recovering by treating missing arrays as length 0 is fine for a
  // mini-preview footer.
  const nodeCount = g.nodes?.length ?? 0;
  const edgeCount = g.edges?.length ?? 0;
  return `${nodeCount} nodes · ${edgeCount} edges`;
}

/**
 * `n` random bytes rendered as lowercase hex. Used by `NewWorkflowButton`
 * to mint a unique default workflow name so consecutive clicks never
 * collide on the API's unique-name constraint. (DEV-16159 followup.)
 */
function randomHex(bytes: number): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
}
